import { useMemo } from 'react';
import { css, type Theme } from '@emotion/react';
import { transparentize } from 'polished';
import { HardDrive } from '@phosphor-icons/react';
import { formatHumanFilesize } from 'common/commonUtil';
import { StatusItemState } from 'common/status/StatusDocument';
import { statusItemStateToTagProps, useStatusItemStateToColor } from 'client/components/Status/statusUtils';
import { StatusTreeMessage } from 'client/components/StatusTree/components/StatusTreeMessage';
import { StatusTreeTagIcon } from 'client/components/StatusTree/components/StatusTreeTagIcon';
import { Tag } from 'client/components/Tag';
import { Typography } from 'client/components/Typography';
import type { ComponentStatusTree } from './componentsStatusUtils';

interface ComponentStorageStatusProps {
  item: ComponentStatusTree['storage'][number];
}

export const ComponentStorageStatus = ({ item }: ComponentStorageStatusProps) => {
  const statusItemStateToColor = useStatusItemStateToColor();

  const { componentName, name, itemStateID, message } = item;
  const sizeBytes = item.sizeBytes ?? 0;
  const estimatedSizeBytes = item.estimatedSizeBytes ?? 0;

  const progress = itemStateID === StatusItemState.GREEN ? (estimatedSizeBytes / sizeBytes) * 100 : 100;
  const tagProps = useMemo(
    () => ({
      ...statusItemStateToTagProps[itemStateID],
      children: (
        <>
          <StatusTreeTagIcon icon={<HardDrive weight="bold" />} itemStateID={itemStateID} />
          {name}
        </>
      ),
    }),
    [itemStateID, componentName, name]
  );

  return (
    <li css={volumeStyle}>
      <div>
        <Tag noWrap {...tagProps} css={statusTagStyle} />
        <div>
          <Typography variant="body2">
            <span />
            <span>
              ~{formatHumanFilesize(estimatedSizeBytes)} / <b>{formatHumanFilesize(sizeBytes)}</b>
            </span>
          </Typography>
        </div>
      </div>
      <div css={progressStyle}>
        <div style={{ width: `${progress}%`, backgroundColor: statusItemStateToColor[itemStateID][100] }} />
      </div>
      <StatusTreeMessage message={message} css={messageStyle} />
    </li>
  );
};

const volumeStyle = (theme: Theme) => css`
  position: relative;
  display: flex;
  flex-direction: column;

  &::before {
    content: '';
    position: absolute;
    display: block;
    width: 4px;
    height: 100%;
    background-color: ${theme.color.green[100]};
    border-radius: 16px;
    left: -30px;
  }

  &:not(:first-of-type) {
    margin-top: 12px;
  }

  > div:first-of-type {
    display: flex;
    align-items: flex-start;

    > label {
      flex-shrink: 0;
      margin-right: 6px;

      > svg {
        margin-right: 4px;
      }
    }

    > div {
      display: flex;
      flex-direction: column;
      flex: 1;

      > p {
        display: flex;
        justify-content: space-between;
      }
    }
  }
`;

const statusTagStyle = css`
  max-width: 70%;
`;

const progressStyle = (theme: Theme) => css`
  display: flex;
  margin-top: 6px;
  height: 6px;
  background-color: ${transparentize(0.5, theme.color.grey[80])};
  border-radius: 6px;
  opacity: 0.8;
  overflow: hidden;

  > div {
    background-color: ${theme.color.primary[100]};
  }
`;

const messageStyle = css`
  margin-top: 4px;
`;
